import {
  Component,
  Element,
  Event,
  type EventEmitter,
  h,
  Host,
  type JSX,
  Listen,
  Method,
  Prop,
  State,
  Watch
} from '@stencil/core';
import {
  GuidedApiQuickLink,
  GuidedApiSuggestion,
  type GuidedSearchCompleted,
  type GuidedSearchHighlighting, type GuidedSearchLoaded,
  type GuidedSearchUserTyping
} from './api';
import type {FormEvent, KeyboardEvent, MouseEvent} from 'react';
import {compareTwoStrings, getFromJsonOrFallback} from './backends/utils';
import {SeeMoreLinks} from './subcomponents/see-more-likns';
import {Suggestions} from './subcomponents/suggestions';
import {QuickLinks} from './subcomponents/quick-links';
import {
  orderDataByPredefinedOrderingRules,
  PromiseResolvers,
  selectCorrectClassForSelectedItem,
  selectCorrectPreselectedItem
} from './subcomponents/utils';
import {type GuidedBackend} from './backends/abstract';
import {withLimiting} from './subcomponents/roundrobin';
import gsTranslations from './guided_translations.json';
import {makeLogger} from '../../logger/logger';
import guidedSearchStore from './guided-search-store';
import type {CtaBannerProps} from '../widgets/cta-banner/cta-banner';
import {SearchHistory} from './subcomponents/search-history';

//DO NOT IMPORT node:timers to have clearTimeout!!!

//DO NOT IMPORT `performance` its builtin but ts is stupid and dont recognize it
//https://developer.mozilla.org/en-US/docs/Web/API/Performance/now


const {info, debug, warn, err} = makeLogger({pathToCurrentFile: 'components/guided/uiaas-guided.tsx'});

@Component({
  tag: 'uiaas-guided-search',
  scoped: false,
  styleUrl: 'uiaas-guided-search.scss',
})
export class UiaasGuided {
  get showLinks(): boolean {
      return this._showLinks;
  }

  set showLinks(value: boolean) {
      this._showLinks = value;
  }

  private totalHits;

  private rightToLeft = false;
  @Element() element: HTMLElement;

  @Prop() readonly language: string;
  @Prop() readonly country?: string;

  //Format: {translations: { 'en':{key1: 'label1'}, 'zh':{key1:'label1} }  }
  @Prop() readonly translations: string = '{}';

  /**
   * Enables smart country selector
   *! Remove after global decommissioning
   */
  @Prop() readonly isSmartCountrySelectorActive: boolean = false;
  @Prop() readonly isConversationalSearchActive: boolean = false;
  @Prop() readonly ctaBannerConfig: CtaBannerProps;
  @Prop() readonly redirectBannerConfig: CtaBannerProps;

  @Prop() readonly labelProvider?: labelProviderInterface = (name, fallback) => {
      const globalTrans = gsTranslations?.translations ?? {};
      const propsTrans = JSON.parse(this.translations)?.translations ?? {};

      const cl = {
        ...globalTrans?.[this.language] ?? {},
        ...propsTrans?.[this.language] ?? {},
      };
      const en = {
        ...globalTrans?.['en'] ?? {},
        ...propsTrans?.['en'] ?? {},
      };

      if (cl.hasOwnProperty(name)) {
        return cl[name];
      } else if (en.hasOwnProperty(name)) {
        return en[name];
      } else {
        return fallback;
      }

  };

  /**
   * Minimal length of search term to trigger the fetching*/
  @Prop() readonly minLength = 3;

  /**
   * Should the component add backdrop to dim rest of the page when guided is active?
   */
  @Prop() readonly enableBackdrop: boolean = true;

  /**
   * Enable the background scroll when the guided is active and the backdrop is visible
   */
  @Prop() readonly backgroundScroll: boolean = false;


  /*Add <pre> listing details*/
  @Prop() readonly debuggingMode: boolean = false;

  /**
   * Which quicklinks sections are enabled?
   * If backend returns result from unknown section, it stays hidden.
   * This field affects the ordering of quicklink sections!
   */
  @Prop() readonly quickLinkSections: string = [
    'recommended', //to match with WMS
    'product-categories',
    'product-lines',
    'products',
    'product-datasheets',
    'documentation-and-support',
    'documents-and-software',
    'documents',
    'software',
    'faq',
    'support',
    'other', //to match with WMS
    'digital-products', //added for exchange
    'communities',
    'developer-resources',
    'orders',
    'quotes'
  ].join(',');

  /**
   * Stringified JSON object, {[string]:number}
   * When empty - doesn't limit anything
   * When contains multiple {name1:count1, name2:count2}, limits the number of returned GS items to
   * summaric amount of items, while trying to fill provided sections, if unable, passess items from sections that
   * have no limit to fill the space.
   */
  @Prop() readonly limits = '{}';

  /**
   * Enable if you are using multiple backends to feed single section,
   * results should be sorted according to relevance. Somehow.
   * Otherwise user will see results only from the topmost backend.
   */
  @Prop() readonly sortResults: boolean = false;
  /**
   * Debounce time when user is typing before sending the ajax query. In ms.
   */
  @Prop() readonly debounceTime: number = 1000;
  /**
   * Handle for setTimeout used in debounce mechanism
   * @private
   */
  private debounce: NodeJS.Timeout;


  /**
   * When assigned, will be called with data of the synthetic event representing click in guided search link
   * If returns true, the component will go to the link
   * If returns false, no action will be taken by the component
   */
  @Prop({mutable: true}) resultClickHandler;

  /**
   * Technical event, sent when component is ready and operative
   */
  @Event({
    eventName: 'guidedSearchMount', //name without : because its conflicting with svelte syntax
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchMount: EventEmitter;
  /**
   * Technical event. Sent when ajax returns results
   */
  @Event({
    eventName: 'guided:SearchLoaded',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchLoaded: EventEmitter<GuidedSearchLoaded>;

  //event can be default prevented by the page, if its not - we redirect the user
  /**
   * Technical event. Sent when user clicks a link or submits the form
   */
  @Event({
    eventName: 'guided:SearchCompleted',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchCompleted: EventEmitter<GuidedSearchCompleted>;


  /**
   * Sent up the DOM when user types
   */
  @Event({
    eventName: 'guided:UserTyping',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchUserTyping: EventEmitter<GuidedSearchUserTyping>;


  /**
   * Sent up the DOM when user changes the highlighted link with mouse or keyboard
   */
  @Event({
    eventName: 'guided:UserHighlighting',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchLinkHighlighting: EventEmitter<GuidedSearchHighlighting>;

  /**
   * Send event when user clicks on history search
   */
  @Event({
    eventName: 'guided:historyClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchHistoryClicked: EventEmitter<string>;


  private backends: Array<GuidedBackend> = [];

  @Method()
  async addBackend(backend: GuidedBackend): Promise<void> {
      this.backends.push(backend);
  }


  @Method()
  async updateHistory() {
      const childrenCount = this.element.children.length;
      for (let i = 0; i < childrenCount; i++) {
        const child = this.element.children[i];
        if (!child.tagName.toLowerCase().startsWith('uiaas-guided-api-'))
          continue;

        if (child.tagName.toLowerCase().includes('search-history')) {
          const historyBackend = child as unknown as GuidedBackend;
          (async () => {
              const response = await historyBackend.fetch('*');
              this.searchHistoryItems = response;
          })();
        }
      }
  }

  /**
   * A converter function that can be assigned from the outside to change all the guided items when they are rendered
   * think of it as a middleware but just for data. noop converter is: (item)=>item
   * @param {CallableFunction} callable (item)=>item
   */
  @Method()
  async addConverter(callable: (item: GuidedApiQuickLink | GuidedApiSuggestion) => void): Promise<void> {
      this.converters.push(callable);
      return;
  }

  /**
   * Enable to show 'See more results' link in the bottom
   */
  @Prop() readonly seeMoreResults: boolean = false;
  @Prop() readonly seeMoreResultsPattern: string = 'https://www.se.com/$CC/$LL/search?q=$TERM';

  /**
   * Enables right to left view
   */
  @Prop() readonly rtl: boolean;
  @Prop() readonly titlesOff: boolean = false;
  @Prop() readonly appName?: string;

  @State() private _showLinks = true;
  private converters = [];

  componentWillLoad(): void {
      //bind API handlers
      const childrenCount = this.element.children.length;
      for (let i = 0; i < childrenCount; i++) {

        const child = this.element.children[i];

        if (!child.tagName.toLowerCase().startsWith('uiaas-guided-api-'))
          continue;

        if (child.tagName.toLowerCase().includes('search-history')) {
          const historyBackend = child as unknown as GuidedBackend;
          (async () => {
              const response = await historyBackend.fetch('*');
              this.searchHistoryItems = response;
              this.showGuidedPane = true;
          })();
        } else {
          this.backends.push(child as unknown as GuidedBackend);
        }
      }

      this.reset();

      const form = this.element.querySelector('form');
      if (form && !form.classList.contains('search-bar__form')) {
        form.classList.add('search-bar__form');
      }

      this.eventEmitterGuidedSearchMount.emit({});
  }


  connectedCallback() {
      if (!this.resultClickHandler) {
        this.resultClickHandler = () => true;
      }

      const body = this.element.closest('body');
      const html = this.element.closest('html');

      if (html) {
        const rtl = html.getAttribute('dir');
        if (rtl === 'rtl') {
          this.rightToLeft = true;
        }
      }

      if (body) {
        const rtl = body.getAttribute('dir');
        if (rtl === 'rtl') {
          this.rightToLeft = true;
        }
      }

      if (this.rtl) {
        this.rightToLeft = true;
      }
  }

  componentDidRender() {
      const guidedPane = this.element?.querySelector('.guided-pane');

      selectCorrectPreselectedItem(guidedPane, this.preselectedLink, this.showGuidedPane);
  }

  // keyup - key is released, captures full term - this is what we want
  // Keydown - happens when the key is pressed down, and auto repeats if the key is pressed down for long.
  // keypress - This event is fired when an alphabetic, numeric, or punctuation key is pressed down.
  //
  // note that keydown and keypress don't see the updated form value!

  @Listen('keyup', {capture: true, passive: false})
  private keyListener(ev: KeyboardEvent) {
      switch (ev.key) {
      case 'Control':
      case 'Shift':
        break;
      case 'ArrowUp':
        ev.preventDefault();
        ev.stopPropagation();
        this.decreaseHighlightIndex();
        break;
      case 'ArrowDown':
        ev.preventDefault();
        ev.stopPropagation();
        this.increaseHighlightIndex();
        break;
      case 'Escape':
        this.blur(null);
        break;
      default:
        if (this.debounce !== null) {
          clearTimeout(this.debounce);
          this.debounce = null;
        }
        this.debounce = setTimeout(() => {
            this.inputChanged(ev);
            this.debounce = null;
        }, this.debounceTime);
      }

      selectCorrectClassForSelectedItem(this.element?.querySelector('.guided-pane'), this.preselectedLink);
  }

  @Listen('reset', {capture: true})
  resetButton(_e: FormEvent): void {
      this.reset();
      (this.element.querySelector('[type="search"]') as HTMLElement)?.focus();
  }

  @State() mouseHoversPane = false;

  private mouseInPane(_e) {
      this.mouseHoversPane = true;
  }

  private mouseOutPane(_e) {
      this.mouseHoversPane = false;
  }

  @Listen('closeButtonClicked', {target: 'window'})
  hideGuidedSearch() {
      this.showGuidedPane = false;
      this.showBackdrop = false;
  }

  @Listen('blur', {capture: true, passive: false})
  blur(e): void {
      if (!this.mouseHoversPane && e?.srcElement?.localName !== 'uiaas-smart-country-selector-banner') {
        this.showGuidedPane = false;
        this._showLinks = true;
        this.showBackdrop = false;
      }
  }

  @Listen('focus', {capture: true})
  focus(e): void {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'QDS-INPUT') {
        this.showGuidedPane = true;
        this.showBackdrop = true;

        if (this.quicklinks.length > 0 || this.suggestions.length > 0) {
          this._showLinks = true;
          this.showLinks = true;
          this.showHistory = false;
        }

        if ((this.quicklinks.length == 0 && this.suggestions.length == 0)) {
          if (this.searchHistoryItems.length > 0) {
            this.showHistory = true;
          }
        }
      }

      if (e.target.tagName === 'SVS-GUIDED-DROPDOWN') {
        this.showHistory = false;
        if (this.quicklinks.length > 0 || this.suggestions.length > 0) {
          this.showGuidedPane = true;
          this._showLinks = false;
          this.showBackdrop = true;
        } else {
          this.showGuidedPane = true;
          this._showLinks = true;
          this.showBackdrop = true;
        }
      }
  }

  @Listen('click', {})
  private handleClick(event: MouseEvent) {
      info('guidedClick unbound handler', event);

      const actualTerm = (event.target as HTMLInputElement).value;

      if (actualTerm !== undefined) {
        guidedSearchStore.set('gsTerm', actualTerm);
      }

      // NOTE: click on guided links are handled by clickHandler!
      // NOTE: on mobile it's up to hosting application to replace the 'Search' button with the 'Cancel' button
  }

  //pressing enter sends both "search" and "submit" and it causes double events
  //when user uses keyboard to navigate the list
  // So the handler - is disabled!!!
  //    @Listen('search', {capture: true})
  //    private handleSearch(event: Event) {
  // event.stopPropagation();
  // event.preventDefault();
  // }
  @Listen('submit', {capture: true})
  handleSubmit(event: FormEvent): void {
      //when there is no keyword typed yet but user clicks on the search button,
      //we do nothing. Original behavior is to submit the form - and move the user
      //to the search results page. Empty page - because there is no keyword.
      //in other words, keyword is required
      const input = (this.element.querySelector('[type="search"]') as HTMLFormElement)
      || this.element.querySelector('qds-input[name="q"]');

      if (input) {
        if (input.value == '') {
          event.preventDefault();
          return;
        }

        input.blur();
      }

      this.showGuidedPane = false; //if you have any scenario that requires NOT to hide guided pane when enter is pressed
      this.showBackdrop = false;
      this.mouseHoversPane = false; //move this two lines lower in this method, and make them conditional
      // => run when default behavior is not prevented on search completed emitter

      if (this.preselectedLink === -1) { // there is no preselected link
      //we let the browser take the default action and redirect the user to the search page with keyword they typed
      //Optional: apply autocorrection if available? How to show that there is an autocorrection?
        this.eventEmitterGuidedSearchCompleted.emit({
          term: this.term,
          link: null
        });
        return;
      }
      event.preventDefault();
      const selectedItem = this.getSelectedLink();
      const searchEvent = this.eventEmitterGuidedSearchCompleted.emit({
        term: this.term,
        link: selectedItem,
        event: 'guidedSearchClick',
        section: selectedItem.sectionId,
      });
      //the result click handler can return false and tell to ignore the click
      //event can be default prevented by the page, if its not - we redirect the user
      if (this.resultClickHandler(event, selectedItem) && !searchEvent.defaultPrevented) {
      // @ts-ignore
        window.location.href = selectedItem.openUrl;
      }
  }

  @Listen('search:completed', {target: 'document'})
  searchCompletedEvent(e) {
      const detail = e.detail;

      if (detail.tab === 'se/all') this.totalHits = detail.count;
  }

  private inputChanged(ev: KeyboardEvent): void {
      //NOTE: it might be better to handle the 'change' event, not keydown
      //      to retrigger the search, up to later consideration
      const inputTag = (ev.target as HTMLElement).tagName;
      if (inputTag === 'INPUT' || inputTag === 'QDS-INPUT') {
      // Without this condition, Guided Search getting value from selected items
      // when user navagiate with tab and this change term to undefined
        this.term = (ev.target as HTMLInputElement).value;

        this.eventEmitterGuidedSearchUserTyping.emit({
          term: this.term,
        });

        guidedSearchStore.set('gsTerm', this.term);
      }
  }

  @State() private term: string;

  @Prop({mutable: true}) showBackdrop = false;

  // @Debounce(250)
  @Watch('term')
  private async fetchResults(newTerm, oldTerm = null) {
      if (newTerm === undefined)
        return;
      if (newTerm == oldTerm)
        return;
      if (newTerm.length < this.minLength)
        return;

      const guidedLoadingStartTimestamp = performance.now(); //performance is built in browser but ts is stupid
      //https://developer.mozilla.org/en-US/docs/Web/API/Performance/now

      //*
      //collect all the suggestions and assign them into their appropriate groups,
      //quicklinks to quicklinks, and suggestions to suggestions
      let backends = this.backends
        .filter(api => !api.disabled);
      // fallback
      if (!backends.length) {
        backends = this.backends;
      }
      const resolvedPromise = await Promise.allSettled(
        backends.map(api => {
            try {
              return api.fetch(this.term);
            } catch (e) {
              err(e, api);
              throw e;
            }
        })
      );

      const results: Array<GuidedApiSuggestion | GuidedApiQuickLink>
      = resolvedPromise
        .filter(PromiseResolvers.isFulfilled)
        .flatMap(p => p.value)
    ;

      const error = resolvedPromise.filter(PromiseResolvers.isRejected);
      if (error && error.length) {
        warn('Failed Fetch promises:', error);
      }

      /*
  //*Note:* allSettled waits for all promises to resolve, so we need to wrap them in a race with timeout
  //        so we won't wait for blue moon till backend returns the data
  // in ideal solution we should cancel the promise and the ajax request when it timeouts
  //This is the draft:
  const timeout = 15_000; //ms
  const results = await Promise.allSettled(this.backends.map(async api=>{
      return await Promise.race([
        new Promise((resolve, reject)=>setTimeout(reject, timeout)),
        new Promise((resolve, reject)=>{
            try{
              api
                .fetch(this.term)
                .then(response=>{ //typescript cries about 'then'
                    resolve(response);
                }).catch(error=>{
                    reject(error);
                });
            }catch(e){
              err(e, api);
              reject(e);
              throw e;
            }
        }),
      ]);
  }));
  //*/
      //alternative version: https://stackoverflow.com/questions/48577702/setting-a-timeout-for-each-promise-within-a-promise-all
      //alternative version, bases on utils / PromiseTimeout

      this.reset();

      debug('Fetch returned', results);

      const limits = getFromJsonOrFallback({data: this.limits, fallback: {}});

      info('Raw results: ', results);

      const filteredResults = withQuicklinkFiltering(results, this.quickLinkSections);

      withLimiting(limits, filteredResults).map(result => { //apply converters so that items can be manipulated in similar way to middleware
          if (this.converters.length == 0)
            return result;
          let r = result;
          this.converters.forEach(conv => {
              r = conv(r);
          });
          return r;
      }).filter(item => item) //if converter returns null, drop that item
        .forEach(result => {
            if (result instanceof GuidedApiQuickLink) {
              this.quicklinks.push(result);
            } else {
              this.suggestions.push(result as GuidedApiSuggestion);
            }
        });

      debug('guided after filtering', {
        quicklinks: this.quicklinks,
        suggestions: this.suggestions
      });

      //this is required when there are 2 or more backends sharing sections,
      //otherwise results from 1st backend always come first
      if (this.sortResults) {
        this.quicklinks = this.quicklinks
          .sort(
            (a, b) => compareTwoStrings(a.title, b.title)
          );

        this.suggestions = this.suggestions
          .sort(
            (a, b) => compareTwoStrings(a.title, b.title)
          );
      }

      const guidedLoadingEndTimestamp = performance.now();

      this.eventEmitterGuidedSearchLoaded.emit({
        term: this.term,
        duration_ms: guidedLoadingEndTimestamp - guidedLoadingStartTimestamp,
      });

  }

  @State() searchHistoryItems: Array<GuidedApiSuggestion> = [];
  @State() suggestions: Array<GuidedApiSuggestion> = [];
  @State() quicklinks: Array<GuidedApiQuickLink> = [];
  @State() showGuidedPane = false;
  /**
   * -1 when no preselection
   * 0 when first link is selected
   * 9 when 10th link is selected
   */
  @State() private preselectedLink: number = -1; //altered by keydown events


  @State() private showHistory: boolean = true;

  /**
   * Clear all suggestions, reset the preselected link
   */
  @Method()
  public async reset() {
      this.suggestions = [];
      this.quicklinks = [];

      const guidedPane = this.element?.querySelector('.guided-pane');

      selectCorrectPreselectedItem(guidedPane, this.preselectedLink, this.showGuidedPane, false);

      this.preselectedLink = -1;
      this._showLinks = true;
      this.showHistory = (this.term === '' || this.term === undefined) && this.searchHistoryItems.length > 0;
  }

  /**
   * Clear term
   */
  @Method()
  public async clearTerm() {
      this.term = '';
  }

  /**
   * Reload suggestions for the same term. i.e category has changed
   */
  @Method()
  public async refresh() {
      this.reset();
      return this.fetchResults(this.term, null);
  }

  private renderGuidedPane() {
      if (!this.showGuidedPane)
        return <div class="guided-pane guided-pane--hidden-no-focus"></div>;

      if (this.suggestions.length == 0 && this.quicklinks.length == 0 && !this.showHistory) {
        return <div class="guided-pane guided-pane--hidden-no-data">
          <SeeMoreLinks
            enabled={this.seeMoreResults}
            urlPattern={this.seeMoreResultsPattern}
            labelProvider={this.labelProvider}
            term={this.term}/>
        </div>;
      }

      return (<div dir={this.rightToLeft ? 'rtl' : 'auto'}
        onMouseEnter={(e) => this.mouseInPane(e)}
        onMouseLeave={(e) => this.mouseOutPane(e)}
        class="guided-pane"
      >
        {
        // The following code trigger country selector
        // It can not be slot because of issue already fixed in higher Stencil core verions
        //! To remove after global decommissioning
          this.isSmartCountrySelectorActive ?
            <uiaas-smart-country-selector-banner totalHits={this.totalHits} language={this.language} isGuidedSearch={true}
              labelProvider={this.labelProvider}/> : ''
        }
        <uiaas-search-widget-conversational-search-banner-mediator isGuidedVariant={true}
          labelProvider={(e, f) => this.labelProvider(e, f)}
          language={this.language} country={this.country}
          isConversationalSearchActive={this.isConversationalSearchActive}/>
        {this.redirectBannerConfig ? <uiaas-search-widget-redirection-banner fluid label={this.redirectBannerConfig.label}
          url={this.redirectBannerConfig.url}></uiaas-search-widget-redirection-banner> : ''}
        {this.ctaBannerConfig ?
          <uiaas-search-widget-cta-banner fluid label={this.ctaBannerConfig.label} url={this.ctaBannerConfig.url}
            cta={this.ctaBannerConfig.cta}
            icon={this.ctaBannerConfig.icon}></uiaas-search-widget-cta-banner> : ''}

        {this.suggestions.length > 0 ? <Suggestions
          suggestions={this.suggestions}
          labelProvider={(name, fallback) => this.labelProvider(name, fallback)}
          clickHandler={(link, e) => this.clickHandler(link, e, 'suggestions')}
        /> : ''}

        {this.quicklinks.length > 0 ? <QuickLinks
          quicklinks={this.quicklinks}
          sections={this.quickLinkSections}
          labelProvider={(name, fallback) => this.labelProvider(name, fallback)}
          clickHandler={(link, e) => this.clickHandler(link, e, 'quicklinks')}
          titlesOff={this.titlesOff}
        /> : ''}

        {(this.searchHistoryItems.length > 0 && this.showHistory) ?
          <SearchHistory
            history={this.searchHistoryItems}
            labelProvider={(name, fallback) => this.labelProvider(name, fallback)}
            clickHandler={(link, e) => this.clickHandler(link, e, 'history')}
          >
          </SearchHistory> : ''}

        <SeeMoreLinks
          enabled={this.seeMoreResults}
          urlPattern={this.seeMoreResultsPattern}
          labelProvider={this.labelProvider}
          term={this.term}/>
      </div>);
  }

  render(): JSX.Element {
      const links = this.renderGuidedPane();

      return (
        <Host class={{'guided-host__input-active': this.showGuidedPane && this.enableBackdrop}}>
          {this.enableBackdrop ?
            <div class={{'guided__backdrop': true, 'guided__backdrop--visible': this.showBackdrop}} onClick={(e) => {
                this.removeBackdrop(e);
            }}/> : ''}
          {(this.showGuidedPane && this.enableBackdrop) ?
            <div class="backdrop-fix">
              <slot></slot>
            </div>
            : <slot></slot>}

          {this._showLinks ? links : ''}
        </Host>
      );
  }


  private removeBackdrop(e): void {
      e.stopPropagation();
      const searchMenuWrapper = document.querySelector('.search-menu-wrapper');
      const cancelButton = document.getElementById('search-bar__cancel');
      this.showBackdrop = false;
      if (this.enableBackdrop && searchMenuWrapper) {
        searchMenuWrapper.classList.remove('show-search-above-mobile-header');

        if (this.backgroundScroll) {
          document.querySelector('body').classList.remove('guided__no-scroll');
        }
        if (cancelButton) {
          cancelButton.style.display = 'none';
        }
      }
  }

  private totalNumberOfLinks(): number {
      return this.suggestions.length + this.quicklinks.length;
  }

  private decreaseHighlightIndex() {
      this.preselectedLink -= 1;
      if (this.preselectedLink < -1)
        this.preselectedLink = this.totalNumberOfLinks() - 1;

      this.eventEmitterGuidedSearchLinkHighlighting.emit({
        term: this.term,
        index: this.preselectedLink,
        link: this.getSelectedLink()
      });
  }

  private increaseHighlightIndex() {
      this.preselectedLink += 1;
      if (this.preselectedLink >= this.totalNumberOfLinks())
        this.preselectedLink = -1;

      this.eventEmitterGuidedSearchLinkHighlighting.emit({
        term: this.term,
        index: this.preselectedLink,
        link: this.getSelectedLink()
      });
  }


  private clickHandler(link: GuidedApiQuickLink & GuidedApiSuggestion, e: MouseEvent, fromWhere: 'quicklinks' | 'suggestions' | 'history'): void {
      info('guidedClick link-bound clickHandler');
      const isEventContextMenu = e.type === 'contextmenu';

      if (!isEventContextMenu) {
        e.stopPropagation();
        e.preventDefault();
      }

      let handleTheClickResult = true;

      if (this.resultClickHandler) {
        handleTheClickResult = this.resultClickHandler(e, link);
      }

      if (!handleTheClickResult) {
      //external handler returned false => we don't follow the link
        info('guided search, app prevented guided from opening the link');
        return;
      }

      //finally emit the technical event
      let emitted;
      if (fromWhere === 'suggestions') {
        emitted = this.eventEmitterGuidedSearchCompleted.emit({
          event: 'guidedSearchClick',
          term: this.term,
          section: 'Most popular searches',
          link: link
        });
      }

      if (fromWhere === 'quicklinks') {
        this.eventEmitterGuidedSearchCompleted.emit({
          event: 'search',
          term: this.term,
          section: link.sectionId,
          link: link
        });

        emitted = this.eventEmitterGuidedSearchCompleted.emit({
          event: 'guidedSearchClick',
          term: this.term,
          section: link.sectionId,
          link: link,
          openUrl: link.openUrl
        });
      }

      if (fromWhere === 'history') {
        this.eventEmitterGuidedSearchHistoryClicked.emit(link.description);
        this.term = link.description;
        this.showGuidedPane = false;
        this.showBackdrop = false;
        this.showHistory = false;
        return;
      }

      // and if it's not defaultPrevented, and resultClickHandler returned true -
      // we redirect the user
      if (handleTheClickResult && !emitted.defaultPrevented && !isEventContextMenu) {
        this.showGuidedPane = false;
        window.location.href = link.openUrl;
      }
  }


  /**
   * Returns the link that's currently highlighted (by keyboard arrows)
   * or null, when nothing is selected
   * @private
   */
  private getSelectedLink(): GuidedApiQuickLink | GuidedApiSuggestion | null {
      if (this.preselectedLink == -1)
        return null;
      return [...this.suggestions, ...orderDataByPredefinedOrderingRules(this.quicklinks, this.quickLinkSections)][this.preselectedLink];
  }
}

const withQuicklinkFiltering = (results, enabledSections: string) => {
    //if no section is enabled, we return without filtering
    if (!enabledSections)
      return results;

    //convert string into proper array
    const sections = enabledSections.split(',');

    return results.filter(item => {

        const isSuggestion = item instanceof GuidedApiSuggestion; //by design accept suggestions, it's a fixed condition
        if (isSuggestion) {
          return true;
        }
        //if item belongs to a section that's enabled, we return true
        return sections.includes(item.sectionId);
    });
};
